(() => {
  "use strict";

  const namespace = "GuruNativeProductForm";
  if (window[namespace]?.version === 2) return;

  const nativeFormSelector = 'product-form form[action*="/cart/add"]';
  const guruOwnedSelector = "[data-guru-product-card-form]";
  const purchaseControlSelector = [
    "[data-guru-option-button]",
    "[data-guru-option-select]",
    "[data-guru-purchase-type]",
    "[data-guru-selling-plan-select]",
    "[data-quantity-decrease]",
    "[data-quantity-increase]",
    "[data-quantity-select]",
    "[data-quantity-number]",
    "[data-main-add]",
    "[data-sticky-add]",
    "[data-guru-linked-products]",
  ].join(",");
  const reconciliationDelays = Object.freeze([0, 200, 600, 1_200]);
  const networkTimeoutDefault = 10_000;
  const networkTimeoutMinimum = 50;
  const networkTimeoutMaximum = 15_000;
  const cartErrorBodyTimeoutDefault = 250;
  const cartErrorBodyTimeoutMinimum = 25;
  const cartErrorBodyTimeoutMaximum = 1_000;
  const cartErrorDescriptionMaximum = 500;
  const unsafeCartErrorDescription = /[<>\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/u;
  const states = new WeakMap();
  const leaseNamespace = "GuruShopCommerceLease";
  const leaseEvent = "guru:commerce-lease:change";
  const cartRootSelector = "[data-guru-cart-drawer], [data-guru-cart-page]";
  let activeOperation = null;
  const fallbackMessages = Object.freeze({
    cartAddError: "The product could not be added to cart. Try again.",
    cartStateUncertain: "The cart could not be confirmed. Refresh the cart before trying again.",
    noVariantData: "No variant data is available for this product.",
  });

  const createCommerceLease = () => {
    let active = null;
    let sequence = 0;
    const notify = () => {
      document.dispatchEvent(new CustomEvent(leaseEvent, {
        detail: {
          kind: active?.kind || "",
          locked: active !== null,
        },
      }));
    };

    return Object.freeze({
      version: 1,
      acquire(owner, kind = "commerce-mutation") {
        if (!owner || active !== null) return null;
        active = Object.freeze({ id: ++sequence, kind, owner });
        notify();
        return active;
      },
      isLocked(token = null) {
        return active !== null && active !== token;
      },
      owns(token) {
        return active !== null && active === token;
      },
      release(token) {
        if (active === null || active !== token) return false;
        active = null;
        notify();
        return true;
      },
    });
  };

  const existingCommerceLease = window[leaseNamespace];
  const commerceLease = existingCommerceLease?.version === 1
    && typeof existingCommerceLease.acquire === "function"
    && typeof existingCommerceLease.isLocked === "function"
    && typeof existingCommerceLease.owns === "function"
    && typeof existingCommerceLease.release === "function"
    ? existingCommerceLease
    : createCommerceLease();
  window[leaseNamespace] = commerceLease;

  const releaseDefinitiveLease = (state) => {
    if (!state || state.phase !== "idle") return false;
    const released = state.lease ? commerceLease.release(state.lease) : false;
    state.lease = null;
    if (activeOperation === state) activeOperation = null;
    return released;
  };

  const readTranslations = () => {
    try {
      const config = JSON.parse(
        document.getElementById("guru-product-card-config")?.textContent || "{}",
      );
      return config?.translations && typeof config.translations === "object"
        ? config.translations
        : {};
    } catch (_error) {
      return {};
    }
  };

  const translations = readTranslations();
  const message = (key) => (
    typeof translations[key] === "string" && translations[key].trim()
      ? translations[key]
      : fallbackMessages[key]
  );

  const route = (path = "") => {
    const root = window.Shopify?.routes?.root || "/";
    return `${root.endsWith("/") ? root : `${root}/`}${String(path).replace(/^\/+/, "")}`;
  };

  const networkTimeoutFor = (form) => {
    const owner = form?.closest?.("[data-guru-product-page]");
    const requested = Number(
      form?.dataset?.guruNetworkTimeout
      || owner?.dataset?.guruNetworkTimeout,
    );
    return Number.isFinite(requested)
      ? Math.min(networkTimeoutMaximum, Math.max(networkTimeoutMinimum, requested))
      : networkTimeoutDefault;
  };

  const cartErrorBodyTimeoutFor = (form) => {
    const owner = form?.closest?.("[data-guru-product-page]");
    const requested = Number(
      form?.dataset?.guruCartErrorBodyTimeout
      || owner?.dataset?.guruCartErrorBodyTimeout,
    );
    return Number.isFinite(requested)
      ? Math.min(
        cartErrorBodyTimeoutMaximum,
        Math.max(cartErrorBodyTimeoutMinimum, Math.floor(requested)),
      )
      : cartErrorBodyTimeoutDefault;
  };

  const withNetworkDeadline = async (duration, request) => {
    const controller = new AbortController();
    let timer = 0;
    const deadline = new Promise((_, reject) => {
      timer = window.setTimeout(() => {
        reject(new Error(message("cartStateUncertain")));
        controller.abort();
      }, duration);
    });

    try {
      // The deadline races the complete request task, including body parsing.
      // Request tasks return data only; late aborted completions therefore have
      // no stateful continuation after the deadline wins.
      return await Promise.race([
        Promise.resolve().then(() => request(controller.signal, controller)),
        deadline,
      ]);
    } finally {
      window.clearTimeout(timer);
    }
  };

  const readCart = (timeout) => withNetworkDeadline(timeout, async (signal) => {
    const response = await fetch(route("cart.js"), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!response.ok) throw new Error(message("cartStateUncertain"));
    const cart = await response.json();
    if (!cart || !Array.isArray(cart.items) || !Number.isFinite(Number(cart.item_count))) {
      throw new Error(message("cartStateUncertain"));
    }
    return cart;
  });

  const stableProperties = (entries) => JSON.stringify(
    [...entries]
      .filter(([, value]) => (
        value !== null
        && value !== undefined
        && String(value).trim() !== ""
      ))
      .map(([key, value]) => [String(key), String(value)])
      .sort(([left], [right]) => left.localeCompare(right)),
  );

  const cartLineIdentity = (item) => {
    const variantId = Number(item?.variant_id || item?.id);
    if (!Number.isSafeInteger(variantId) || variantId < 1) return "";
    const sellingPlanId = String(item?.selling_plan_allocation?.selling_plan?.id || "");
    return JSON.stringify([
      String(variantId),
      sellingPlanId,
      stableProperties(Object.entries(item?.properties || {})),
    ]);
  };

  const canonicalPositiveInteger = (value) => {
    if (typeof value !== "string" || !/^[1-9]\d*$/.test(value)) return null;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && String(parsed) === value ? parsed : null;
  };

  const snapshotCommerceRequest = (form) => {
    let formData;
    try {
      // This must remain the first asynchronous-operation-independent snapshot.
      // The exact object is sent after the baseline GET, so later DOM changes
      // cannot turn the user's click for commerce payload A into payload B.
      formData = new FormData(form);
    } catch (_error) {
      return null;
    }

    const variantValues = formData.getAll("id");
    const quantityValues = formData.getAll("quantity");
    const sellingPlanValues = formData.getAll("selling_plan");
    if (
      variantValues.length !== 1
      || quantityValues.length !== 1
      || sellingPlanValues.length > 1
    ) return null;

    const variantId = canonicalPositiveInteger(variantValues[0]);
    const quantity = canonicalPositiveInteger(quantityValues[0]);
    const sellingPlanValue = sellingPlanValues[0] ?? "";
    if (
      variantId === null
      || quantity === null
      || typeof sellingPlanValue !== "string"
      || (sellingPlanValue !== "" && canonicalPositiveInteger(sellingPlanValue) === null)
    ) return null;

    const properties = [];
    const propertyNames = new Set();
    const emptyPropertyFields = [];
    for (const [key, value] of formData.entries()) {
      if (key.startsWith("properties[") && !/^properties\[(.+)]$/.test(key)) return null;
      const match = key.match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (value instanceof File && value.size > 0) return null;
      if (propertyNames.has(match[1])) return null;
      propertyNames.add(match[1]);
      const normalizedValue = value instanceof File ? value.name : String(value);
      if (normalizedValue.trim() === "") {
        emptyPropertyFields.push(key);
        continue;
      }
      properties.push([match[1], normalizedValue]);
    }
    emptyPropertyFields.forEach((key) => formData.delete(key));

    return {
      formData,
      identity: JSON.stringify([
        String(variantId),
        sellingPlanValue,
        stableProperties(properties),
      ]),
      onSuccessDo: String(formData.get("on_success") || ""),
      quantity,
      sellingPlanId: sellingPlanValue,
      variantId,
    };
  };

  const cartSnapshot = (cart) => {
    const snapshot = new Map();
    for (const item of cart?.items || []) {
      const identity = cartLineIdentity(item);
      const quantity = Number(item?.quantity);
      if (!identity || !Number.isSafeInteger(quantity) || quantity < 1) return null;
      snapshot.set(identity, (snapshot.get(identity) || 0) + quantity);
    }
    return snapshot;
  };

  const snapshotEquals = (left, right) => Boolean(
    left
    && right
    && left.size === right.size
    && [...left].every(([identity, quantity]) => right.get(identity) === quantity),
  );

  const expectedSnapshot = (baseline, identity, quantity) => {
    if (!baseline || !identity || !Number.isSafeInteger(quantity) || quantity < 1) return null;
    const expected = new Map(baseline);
    expected.set(identity, (expected.get(identity) || 0) + quantity);
    return expected;
  };

  const operationOwnerKey = (form) => {
    const owner = form?.closest?.("[data-guru-product-page]");
    if (owner) {
      const ownerId = String(owner.id || "").trim();
      const productUrl = String(owner.dataset.productUrl || "").trim();
      if (ownerId && productUrl) return JSON.stringify(["pdp", ownerId, productUrl]);
    }
    const formId = String(form?.id || "").trim();
    return formId ? JSON.stringify(["form", formId]) : "";
  };

  const purchaseControls = (form) => {
    const owner = form.closest("[data-guru-product-page]") || form;
    const candidates = new Set([
      ...form.elements,
      ...owner.querySelectorAll(purchaseControlSelector),
    ]);
    return [...candidates].filter((element) => (
      element instanceof HTMLButtonElement
      || element instanceof HTMLInputElement
      || element instanceof HTMLSelectElement
      || element instanceof HTMLTextAreaElement
    ));
  };

  const lockPurchaseControls = (form, state) => {
    if (!(form instanceof HTMLFormElement)) return;
    state.controlLocks ||= new Map();
    if (!state.controlLocks.has(form)) {
      state.controlLocks.set(form, purchaseControls(form).map((element) => ({
        ariaBusy: element.getAttribute("aria-busy"),
        disabled: element.disabled,
        element,
      })));
    }
    state.controlLocks.get(form).forEach(({ element }) => {
      element.disabled = true;
      if (element.matches('button[type="submit"], [data-main-add], [data-sticky-add]')) {
        element.setAttribute("aria-busy", "true");
      }
    });
  };

  const unlockPurchaseControls = (state) => {
    state.controlLocks?.forEach((locks) => {
      locks.forEach(({ ariaBusy, disabled, element }) => {
        if (!element.isConnected) return;
        element.disabled = disabled;
        if (ariaBusy === null) element.removeAttribute("aria-busy");
        else element.setAttribute("aria-busy", ariaBusy);
      });
    });
    state.controlLocks = null;
  };

  const bindOperationForm = (form, state) => {
    if (
      !(form instanceof HTMLFormElement)
      || form.closest(guruOwnedSelector)
      || !form.matches(nativeFormSelector)
    ) return false;
    states.set(form, state);
    state.forms ||= new Set();
    state.forms.add(form);
    lockPurchaseControls(form, state);
    return true;
  };

  const bindReplacementForms = (state) => {
    if (!state?.ownerKey) return;
    document.querySelectorAll(nativeFormSelector).forEach((form) => {
      if (operationOwnerKey(form) === state.ownerKey) bindOperationForm(form, state);
    });
  };

  const connectedOperationForm = (state, preferred = null) => {
    if (
      preferred instanceof HTMLFormElement
      && preferred.isConnected
      && states.get(preferred) === state
    ) return preferred;
    bindReplacementForms(state);
    return [...(state?.forms || [])].find((form) => (
      form.isConnected && states.get(form) === state
    )) || null;
  };

  const stateForForm = (form) => {
    const mapped = states.get(form);
    if (mapped) return mapped;
    if (
      activeOperation?.ownerKey
      && activeOperation.ownerKey === operationOwnerKey(form)
      && commerceLease.owns(activeOperation.lease)
    ) {
      bindOperationForm(form, activeOperation);
      return activeOperation;
    }
    return null;
  };

  const dispatchError = (form, message) => {
    form.dispatchEvent(new CustomEvent("cart:error", {
      bubbles: true,
      detail: { error: message },
    }));
  };

  const readResponseJson = async (response) => {
    try {
      const payload = await response.json();
      return payload && typeof payload === "object" ? payload : null;
    } catch (_error) {
      return null;
    }
  };

  const normalizeResponseJson = (value) => (
    value && typeof value === "object" && !Array.isArray(value) ? value : null
  );

  const cartAddErrorMessage = (responseJson) => {
    const description = typeof responseJson?.description === "string"
      ? responseJson.description.trim()
      : "";
    return description
      && description.length <= cartErrorDescriptionMaximum
      && !unsafeCartErrorDescription.test(description)
      ? description
      : message("cartAddError");
  };

  const readBoundedCartErrorBody = async (response, form, abortBody) => {
    let bodyTimedOut = false;
    let resolveBodyDeadline;
    const bodyDeadline = new Promise((resolve) => {
      resolveBodyDeadline = resolve;
    });
    const bodyTimer = window.setTimeout(() => {
      bodyTimedOut = true;
      try {
        abortBody?.();
      } catch (_error) {
        // A definitive 422 is already released; abort is cleanup only.
      }
      resolveBodyDeadline(null);
    }, cartErrorBodyTimeoutFor(form));
    const bodyRequest = Promise.resolve()
      .then(() => readResponseJson(response))
      .then(normalizeResponseJson);

    try {
      const responseJson = await Promise.race([bodyRequest, bodyDeadline]);
      return bodyTimedOut ? null : responseJson;
    } catch (_error) {
      return null;
    } finally {
      window.clearTimeout(bodyTimer);
    }
  };

  const postAdd = (formData, timeout) => withNetworkDeadline(timeout, async (signal, controller) => {
    const response = await fetch(route("cart/add.js"), {
      method: "POST",
      body: formData,
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      signal,
    });
    const status = Number(response?.status);
    if (!Number.isInteger(status)) throw new Error(message("cartStateUncertain"));
    if (status === 422) {
      return {
        abortBody: () => controller.abort(),
        response,
        responseJson: null,
        status,
      };
    }
    const responseJson = normalizeResponseJson(await readResponseJson(response));
    return { response, responseJson, status };
  });

  const responseContainsVariant = (payload, variantId) => {
    if (!payload) return false;
    const items = Array.isArray(payload.items) ? payload.items : [payload];
    return items.some((item) => Number(item?.variant_id || item?.id) === variantId);
  };

  const sectionIdsForMutation = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
      bubbles: true,
      detail: { sections },
    }));
    return [...new Set(sections.filter((id) => typeof id === "string" && id.trim()))];
  };

  const readFreshSections = async (sectionIds, timeout) => {
    if (sectionIds.length === 0) return {};
    const url = new URL(route(), window.location.origin);
    url.searchParams.set("sections", sectionIds.join(","));
    return withNetworkDeadline(timeout, async (signal) => {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        signal,
      });
      if (!response.ok) throw new Error(message("cartStateUncertain"));
      const sections = await response.json();
      if (!sections || Array.isArray(sections) || typeof sections !== "object") {
        throw new Error(message("cartStateUncertain"));
      }
      return sections;
    });
  };

  const presentRecoveryError = (state) => {
    if (
      activeOperation !== state
      || !state.recoveryMessage
      || state.committingPresentation
      || !commerceLease.owns(state.lease)
    ) return;
    window.GuruShopCartDrawer?.showError?.(
      new Error(state.recoveryMessage),
      () => recoverOperation(state),
      { commerceLeaseRecovery: true },
    );
  };

  const showRecoveryError = (form, state, copy) => {
    state.recoveryMessage = copy;
    const currentForm = connectedOperationForm(state, form);
    if (currentForm) dispatchError(currentForm, copy);
    presentRecoveryError(state);
  };

  const clearRecoveryError = (state) => {
    const wasPresented = Boolean(state?.recoveryMessage);
    if (state) state.recoveryMessage = "";
    if (wasPresented) window.GuruShopCartDrawer?.clearError?.();
  };

  const finishCommitted = async (form, state, cart, { freshSections = false } = {}) => {
    const current = cartSnapshot(cart);
    if (!snapshotEquals(current, state.expected)) {
      state.phase = "uncertain";
      throw new Error(message("cartStateUncertain"));
    }

    if (window.themeVariables?.settings?.cartType === "page" || window.themeVariables?.settings?.pageType === "cart") {
      state.phase = "idle";
      clearRecoveryError(state);
      unlockPurchaseControls(state);
      releaseDefinitiveLease(state);
      window.location.assign(route("cart"));
      return;
    }

    const sections = freshSections
      ? await readFreshSections(state.sectionIds, state.networkTimeout)
      : state.responseJson?.sections;
    const preparedCart = await window.GuruShopCartDrawer?.prepareCart?.(cart, sections);
    if (!preparedCart) throw new Error(message("cartStateUncertain"));
    state.committingPresentation = true;
    try {
      await window.GuruShopCartDrawer.commitCart({
        cart: preparedCart,
        baseEvent: "variant:add",
        onSuccessDo: state.onSuccessDo,
      });
    } finally {
      state.committingPresentation = false;
    }

    if (!state.dispatched && form instanceof HTMLFormElement) {
      state.dispatched = true;
      form.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items: state.responseJson?.items || [state.responseJson].filter(Boolean),
          cart: preparedCart,
          onSuccessDo: state.onSuccessDo,
        },
      }));
    }
    state.phase = "idle";
    clearRecoveryError(state);
    unlockPurchaseControls(state);
    releaseDefinitiveLease(state);
    window.GuruShopCartDrawer?.clearError?.();
  };

  const reconcileDelay = (duration) => new Promise((resolve) => {
    window.setTimeout(resolve, duration);
  });

  const reconcileUncertain = async (form, state) => {
    state.phase = "recovering";
    const currentForm = connectedOperationForm(state, form);
    if (currentForm) lockPurchaseControls(currentForm, state);

    for (const delay of reconciliationDelays) {
      if (delay > 0) await reconcileDelay(delay);
      try {
        const cart = await readCart(state.networkTimeout);
        const current = cartSnapshot(cart);
        if (snapshotEquals(current, state.expected)) {
          state.phase = "committed";
          try {
            await finishCommitted(currentForm, state, cart, { freshSections: true });
          } catch (error) {
            state.phase = "committed";
            showRecoveryError(
              currentForm,
              state,
              error?.message || message("cartStateUncertain"),
            );
          }
          return;
        }
        // An unchanged baseline is not proof that an ambiguous POST was
        // rejected: Shopify may still commit it after this read. Keep polling,
        // then retain uncertain ownership if the bounded window expires.
        if (!snapshotEquals(current, state.baseline)) break;
      } catch (_error) {
        // A failed authoritative read keeps the POST outcome unknown.
      }
    }

    state.phase = "uncertain";
    showRecoveryError(currentForm, state, message("cartStateUncertain"));
  };

  async function recoverOperation(state) {
    if (
      activeOperation !== state
      || !commerceLease.owns(state?.lease)
      || !["committed", "uncertain"].includes(state?.phase)
    ) return false;
    if (state.recoveryPromise) return state.recoveryPromise;

    const recovery = (async () => {
      const form = connectedOperationForm(state);
      if (form) lockPurchaseControls(form, state);
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
      try {
        if (state.phase === "committed") {
          state.phase = "recovering";
          try {
            const cart = await readCart(state.networkTimeout);
            state.phase = "committed";
            await finishCommitted(form, state, cart, { freshSections: true });
          } catch (error) {
            if (state.phase !== "uncertain") state.phase = "committed";
            showRecoveryError(
              form,
              state,
              error?.message || message("cartStateUncertain"),
            );
          }
          return state.phase === "idle";
        }

        await reconcileUncertain(form, state);
        return state.phase === "idle";
      } finally {
        if (state.phase === "idle") {
          unlockPurchaseControls(state);
          releaseDefinitiveLease(state);
        }
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      }
    })();

    state.recoveryPromise = recovery;
    try {
      return await recovery;
    } finally {
      if (state.recoveryPromise === recovery) state.recoveryPromise = null;
    }
  }

  async function submitNativeForm(form) {
    const previous = stateForForm(form);
    if (["preparing", "adding", "recovering"].includes(previous?.phase)) return;
    if (["committed", "uncertain"].includes(previous?.phase)) {
      await recoverOperation(previous);
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Capture and validate every commerce field synchronously in the submit
    // turn, before the baseline cart request yields control back to the page.
    const requested = snapshotCommerceRequest(form);
    if (!requested) {
      dispatchError(form, message("noVariantData"));
      return;
    }

    if (
      window.GuruShopCartDrawer?.isMutationPending?.()
      || commerceLease.isLocked()
    ) {
      dispatchError(form, message("cartStateUncertain"));
      return;
    }

    const operation = {
      phase: "preparing",
      requested,
      lease: null,
      ownerKey: operationOwnerKey(form),
      forms: new Set(),
      controlLocks: new Map(),
      networkTimeout: networkTimeoutFor(form),
      recoveryMessage: "",
      recoveryPromise: null,
      committingPresentation: false,
    };
    operation.lease = commerceLease.acquire(operation, "native-product-add");
    if (!operation.lease) {
      dispatchError(form, message("cartStateUncertain"));
      return;
    }
    activeOperation = operation;
    bindOperationForm(form, operation);
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    try {
      let baselineCart;
      try {
        baselineCart = await readCart(operation.networkTimeout);
      } catch (_error) {
        operation.phase = "idle";
        dispatchError(form, message("cartStateUncertain"));
        return;
      }

      const baseline = cartSnapshot(baselineCart);
      const expected = requested
        ? expectedSnapshot(baseline, requested.identity, requested.quantity)
        : null;
      if (!baseline || !expected) {
        operation.phase = "idle";
        dispatchError(form, message("noVariantData"));
        return;
      }

      Object.assign(operation, {
        phase: "adding",
        baseline,
        expected,
        identity: requested.identity,
        quantity: requested.quantity,
        sellingPlanId: requested.sellingPlanId,
        variantId: requested.variantId,
        sectionIds: sectionIdsForMutation(),
        responseJson: null,
        onSuccessDo: requested.onSuccessDo,
        dispatched: false,
      });
      const state = operation;
      const { formData } = requested;
      formData.set("sections", state.sectionIds.join(","));
      formData.set("sections_url", route(`variants/${state.variantId}`));

      let addResult;
      try {
        addResult = await postAdd(formData, state.networkTimeout);
      } catch (_error) {
        state.phase = "uncertain";
        await reconcileUncertain(form, state);
        return;
      }

      if (addResult.status === 422) {
        state.phase = "idle";
        clearRecoveryError(state);
        window.GuruShopCartDrawer?.clearError?.();
        // Shopify's 422 header definitively rejects this mutation. Release the
        // document lease before reading its optional error body so a body that
        // ignores abort, never settles, or settles late cannot turn rejection
        // into an uncertain operation or authorize a stale continuation.
        releaseDefinitiveLease(state);
        const responseJson = await readBoundedCartErrorBody(
          addResult.response,
          form,
          addResult.abortBody,
        );
        dispatchError(
          form,
          cartAddErrorMessage(responseJson),
        );
        return;
      }

      state.responseJson = addResult.responseJson;

      // A success-shaped body may enrich the later success event, but it never
      // proves ownership. Discard incongruent response details before the same
      // GET-only reconciliation used by every non-422 outcome.
      if (!responseContainsVariant(state.responseJson, state.variantId)) {
        state.responseJson = null;
      }

      // Success-shaped, malformed, other 4xx and 5xx responses are all
      // ambiguous until an authoritative GET proves the exact commerce delta.
      state.phase = "uncertain";
      await reconcileUncertain(form, state);
    } finally {
      if (operation.phase === "idle") {
        unlockPurchaseControls(operation);
        releaseDefinitiveLease(operation);
      }
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  }

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(nativeFormSelector)) return;
    if (form.closest(guruOwnedSelector)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void submitNativeForm(form);
  }, true);

  const rehydrateActiveOperation = () => {
    const state = activeOperation;
    if (!state || !commerceLease.owns(state.lease)) return;
    bindReplacementForms(state);
    (state.forms || []).forEach((form) => {
      if (form.isConnected) lockPurchaseControls(form, state);
    });
    presentRecoveryError(state);
  };

  let rehydrationQueued = false;
  const scheduleOperationRehydration = () => {
    if (rehydrationQueued) return;
    rehydrationQueued = true;
    queueMicrotask(() => {
      rehydrationQueued = false;
      rehydrateActiveOperation();
    });
  };

  document.addEventListener("cart-drawer:refreshed", scheduleOperationRehydration);
  document.addEventListener("guru-cart-page:refreshed", scheduleOperationRehydration);
  document.addEventListener("shopify:section:load", scheduleOperationRehydration);

  const replacementObserver = new MutationObserver((records) => {
    if (!activeOperation) return;
    const containsRelevantReplacement = records.some(({ addedNodes }) => (
      [...addedNodes].some((node) => (
        node instanceof Element
        && (
          node.matches(`${cartRootSelector}, ${nativeFormSelector}`)
          || node.querySelector?.(`${cartRootSelector}, ${nativeFormSelector}`)
        )
      ))
    ));
    if (containsRelevantReplacement) scheduleOperationRehydration();
  });
  replacementObserver.observe(document.documentElement, { childList: true, subtree: true });

  window[namespace] = {
    version: 2,
    ownsRecoveryLease: (form) => {
      const state = form instanceof HTMLFormElement ? stateForForm(form) : null;
      return ["committed", "recovering", "uncertain"].includes(state?.phase)
        && commerceLease.owns(state.lease);
    },
    submit: (form) => submitNativeForm(form),
  };
})();
